/**
 * ScheduleDraftEditorWorkspaceContext — ScheduleDraft 编辑工作台状态组合层。
 *
 * 本 Context 组合 detail query、派生数据模型、本地 UI 状态、mutation 编排和
 * 编辑命令管道，并向页面区块提供统一工作台上下文。它保持薄 Provider 形态，
 * 具体数据、状态、副作用和写操作分别下沉到 hooks/lib/services。
 *
 * 设计文档 → docs/20_workspace_ux/28_frontend_code_map_and_data_flow.md
 * 骨架关联 → hooks/useScheduleDraftEditorData.js (派生模型), hooks/useScheduleDraftEditorUiState.js (UI 状态), hooks/useScheduleDraftEditorMutations.js (写操作), hooks/useScheduleDraftEditPipeline.js (命令管道), lib/editCommandPipeline.js (ChangeSet 构建)
 */

import React, { createContext, useCallback, useEffect, useMemo, useRef } from "react";
import { message } from "antd";
import { debounce } from "lodash";
import { useAuth } from "../../../../hooks/useAuth";
import { useAirportConstraints } from "../../../../hooks/useReferenceData";
import { getAgentAircraftReg, getAgentEvaluationSummary, getAgentStrategyLabel, getAgentTrackStatus, isAgentTrack } from "../lib/agentTrackUtils";
import { getDefaultEditingTrack, getDraftFlights, getTrackFlights } from "../lib/trackUtils";
import useScheduleDraftAnalysis, { buildScheduleDraftAnalysisRequestSignature } from "../hooks/useScheduleDraftAnalysis";
import useScheduleDraftAgentSessions from "../hooks/useScheduleDraftAgentSessions";
import useAircraftAvailabilityWindows from "../hooks/useAircraftAvailabilityWindows";
import useScheduleDraftEditorData from "../hooks/useScheduleDraftEditorData";
import useScheduleDraftEditorMutations from "../hooks/useScheduleDraftEditorMutations";
import useScheduleDraftEditorUiState from "../hooks/useScheduleDraftEditorUiState";
import useScheduleDraftDetail from "../hooks/useScheduleDraftDetail";
import useLocalEditState from "../hooks/useLocalEditState";
import useLocalCompareMatchState from "../hooks/useLocalCompareMatchState";
import { useScheduleDraftEditPipeline } from "../hooks/useScheduleDraftEditPipeline";
import { buildWiringCallbacks, useScheduleDraftEditorSideEffects } from "../hooks/useScheduleDraftEditorSideEffects";
import { buildAircraftAvailabilityQueryInput } from "../lib/aircraftAvailabilityQueryInput";
import { buildChangeSet } from "../lib/editCommandPipeline";
import { dirtyStateAffectsLocalConstraints } from "../lib/fieldImpactUtils";
import { withCalculatedFlightTimings } from "../lib/flightTimingUtils";
import { validateAllFlightConstraints, validateFlightConstraints } from "../lib/validation/flightConstraintValidators";
import { buildTrackWorkingFlightsSnapshot } from "../lib/workingFlightsSnapshot";

export const ScheduleDraftEditorWorkspaceContext = createContext(null);

const AGENT_WORKING_STATUS = "AGENT_WORKING";

function isCompletedAgentStatus(status) {
    return ["COMPLETED", "PASSED", "SUCCESS", "FAILED"].includes(String(status || "").toUpperCase());
}

function withDraftCalculatedFlightTimings(draft, airportConstraintsMap = {}) {
    if (!draft) {
        return draft;
    }

    const tracks = (draft.tracks || []).map((track) => ({
        ...track,
        flights: withCalculatedFlightTimings(getTrackFlights(track), airportConstraintsMap),
    }));
    const draftWithTracks = { ...draft, tracks };

    return {
        ...draftWithTracks,
        flights: withCalculatedFlightTimings(getDraftFlights(draftWithTracks), airportConstraintsMap),
    };
}

function buildAnalysisWorkingFlightsPayload(localEditState, currentEditingTrack, currentEditingTrackId, airportConstraintsMap = {}) {
    if (!currentEditingTrackId) {
        return {};
    }

    const localFlightsMap = localEditState?.localFlightsMapRef?.current;
    const sourceFlights = localFlightsMap?.size
        ? Array.from(localFlightsMap.values())
        : getTrackFlights(currentEditingTrack);
    const timedFlights = withCalculatedFlightTimings(sourceFlights, airportConstraintsMap);

    return {
        working_flights: buildTrackWorkingFlightsSnapshot(timedFlights, currentEditingTrackId),
    };
}

/**
 * 提供 ScheduleDraft 编辑工作台的组合上下文。
 * @param {Object} props - Provider 属性。
 * @param {string|number} props.draftId - 当前草稿 ID。
 * @param {React.ReactNode} props.children - 工作台子树。
 * @returns {React.ReactElement} Context Provider。
 */
export function ScheduleDraftEditorWorkspaceProvider({ draftId, children }) {
    const { hasPermission } = useAuth();
    const rawCanUpdateFlight = hasPermission("schedule:update");
    const rawCanDeleteFlight = hasPermission("schedule:delete");

    const detailQuery = useScheduleDraftDetail(draftId, {
        enabled: Boolean(draftId),
        refetchInterval: (data) => (data?.agent_session_status === AGENT_WORKING_STATUS ? 5000 : false),
    });

    const serverDraft = detailQuery.data;
    const agentSessionStatus = serverDraft?.agent_session_status || "IDLE";
    const isAgentWorking = agentSessionStatus === AGENT_WORKING_STATUS;
    const airportConstraintsQuery = useAirportConstraints({ enabled: Boolean(draftId) });
    const localEditState = useLocalEditState({ airportConstraintsMap: airportConstraintsQuery.constraintsMap });
    const localCompareMatchState = useLocalCompareMatchState();
    const hasLocalWorkingChanges = localEditState.dirtyState.isDirty || localCompareMatchState.dirtyState.isDirty;
    const initializedServerTokenRef = useRef(null);
    const agentTrackStatusRef = useRef({ draftId: null, initialized: false, statuses: new Map() });
    const analysisRequestPayloadRef = useRef({});

    const agentSessionsQuery = useScheduleDraftAgentSessions(draftId, {
        enabled: Boolean(draftId),
        refetchInterval: (data) => (isAgentWorking || data?.agent_session_status === AGENT_WORKING_STATUS ? 5000 : false),
    });

    useEffect(() => {
        if (!serverDraft) {
            return;
        }

        const serverToken = `${serverDraft.id || draftId}:${detailQuery.dataUpdatedAt || "initial"}`;
        if (initializedServerTokenRef.current === serverToken || localEditState.dirtyState.isDirty || localEditState.pendingChangeSet || localEditState.undoStack.length > 0) {
            return;
        }

        localEditState.initFromServer(getDraftFlights(serverDraft));
        initializedServerTokenRef.current = serverToken;
    }, [detailQuery.dataUpdatedAt, draftId, localEditState, serverDraft]);

    const draft = useMemo(
        () => withDraftCalculatedFlightTimings(localEditState.getLocalDraft(serverDraft), airportConstraintsQuery.constraintsMap),
        [airportConstraintsQuery.constraintsMap, localEditState.getLocalDraft, serverDraft],
    );
    const defaultTrackId = getDefaultEditingTrack(draft)?.id || draft?.default_track_id;
    const ui = useScheduleDraftEditorUiState(draft);
    const availabilityQueryInput = useMemo(
        () => buildAircraftAvailabilityQueryInput(draft, {
            visibleReferenceTrackIds: ui.visibleReferenceTrackIds,
            referenceTrackKeys: ui.referenceTrackKeys,
            externalReferenceTracks: ui.externalReferenceTracks,
        }),
        [draft, ui.externalReferenceTracks, ui.referenceTrackKeys, ui.visibleReferenceTrackIds],
    );
    const availabilityWindowsQuery = useAircraftAvailabilityWindows(availabilityQueryInput, {
        enabled: Boolean(draftId && draft),
    });
    const isAircraftAvailabilityValidationPending = Boolean(
        availabilityQueryInput.registrations?.length
        && availabilityQueryInput.start
        && availabilityQueryInput.end
        && (availabilityWindowsQuery.isLoading || availabilityWindowsQuery.error),
    );
    const effectiveObservationMode = ui.observationMode === "pattern" ? "timeline" : ui.observationMode;
    const shouldBuildTimelineProjection = effectiveObservationMode === "timeline" && !ui.timelineCanvasCollapsed;
    const currentEditingTrackId = ui.currentEditingTrackId || defaultTrackId;
    const currentEditingTrack = useMemo(
        () => (draft?.tracks || []).find((track) => String(track.id) === String(currentEditingTrackId)) || getDefaultEditingTrack(draft),
        [currentEditingTrackId, draft],
    );
    const isCurrentEditingTrackWritable = !currentEditingTrack || currentEditingTrack.is_editable !== false;
    const canUpdateFlight = rawCanUpdateFlight && !isAgentWorking && isCurrentEditingTrackWritable;
    const canDeleteFlight = rawCanDeleteFlight && !isAgentWorking && isCurrentEditingTrackWritable;
    const canDeleteTrack = rawCanDeleteFlight && !isAgentWorking;
    const buildCurrentAnalysisRequestPayload = useCallback(
        () => buildAnalysisWorkingFlightsPayload(
            localEditState,
            currentEditingTrack,
            currentEditingTrackId,
            airportConstraintsQuery.constraintsMap,
        ),
        [airportConstraintsQuery.constraintsMap, currentEditingTrack, currentEditingTrackId, localEditState],
    );
    const analysisRequestPayload = useMemo(
        () => buildCurrentAnalysisRequestPayload(),
        [buildCurrentAnalysisRequestPayload, localEditState.localFlightsMap],
    );
    analysisRequestPayloadRef.current = analysisRequestPayload;
    const analysisRequestPayloadSignature = useMemo(
        () => buildScheduleDraftAnalysisRequestSignature(analysisRequestPayload),
        [analysisRequestPayload],
    );

    useEffect(() => {
        if (!serverDraft?.id) {
            return;
        }

        const previous = agentTrackStatusRef.current;
        const isSameDraft = previous.draftId === serverDraft.id;
        const previousStatuses = isSameDraft ? previous.statuses : new Map();
        const nextStatuses = new Map();

        (serverDraft.tracks || []).filter(isAgentTrack).forEach((track) => {
            const trackId = String(track.id);
            const status = getAgentTrackStatus(track);
            nextStatuses.set(trackId, status);

            if (!isSameDraft || !previous.initialized) {
                return;
            }

            const previousStatus = previousStatuses.get(trackId);
            if (isCompletedAgentStatus(status) && previousStatus !== status) {
                const aircraftReg = getAgentAircraftReg(track) || "Agent";
                const strategyLabel = getAgentStrategyLabel(track);
                const notify = String(status || "").toUpperCase() === "FAILED" ? message.error : message.success;
                notify(`${aircraftReg} · ${strategyLabel} 方案已更新（${getAgentEvaluationSummary(track)}）`);
            }
        });

        agentTrackStatusRef.current = {
            draftId: serverDraft.id,
            initialized: true,
            statuses: nextStatuses,
        };
    }, [detailQuery.dataUpdatedAt, serverDraft]);

    const analysisQuery = useScheduleDraftAnalysis(draftId, currentEditingTrackId, {
        requestPayload: analysisRequestPayload,
        requestPayloadRef: analysisRequestPayloadRef,
        enabled: Boolean(draftId && draft && currentEditingTrackId),
    });

    const refetchAnalysisWithCurrentWorkingFlights = useCallback(() => {
        if (!draftId || !currentEditingTrackId) {
            return null;
        }

        analysisRequestPayloadRef.current = buildCurrentAnalysisRequestPayload();
        return analysisQuery.refetch();
    }, [analysisQuery.refetch, buildCurrentAnalysisRequestPayload, currentEditingTrackId, draftId]);

    const debouncedRefreshAnalysis = useMemo(
        () =>
            debounce(() => {
                refetchAnalysisWithCurrentWorkingFlights();
            }, 1500),
        [refetchAnalysisWithCurrentWorkingFlights],
    );

    useEffect(() => {
        return () => debouncedRefreshAnalysis.cancel();
    }, [debouncedRefreshAnalysis]);

    const analysisResultSignature = analysisQuery.data?._requestPayloadSignature;
    const isAnalysisResultCurrent = !analysisResultSignature || analysisResultSignature === analysisRequestPayloadSignature;
    const effectiveAnalysisResult = isAnalysisResultCurrent ? analysisQuery.data : null;

    const hasConstraintAffectingLocalEdits = useMemo(
        () => dirtyStateAffectsLocalConstraints(localEditState.dirtyState),
        [localEditState.dirtyState],
    );
    const localValidationIssues = useMemo(
        () => (hasConstraintAffectingLocalEdits || !isAnalysisResultCurrent
            ? validateAllFlightConstraints(localEditState.localFlightsMap, airportConstraintsQuery.constraintsMap, availabilityWindowsQuery.windowsByRegistration)
            : []),
        [airportConstraintsQuery.constraintsMap, availabilityWindowsQuery.windowsByRegistration, hasConstraintAffectingLocalEdits, isAnalysisResultCurrent, localEditState.localFlightsMap],
    );

    const data = useScheduleDraftEditorData({
        draft,
        selectedItemId: ui.selectedItemId,
        showEditableTracks: ui.showEditableTracks,
        showDemandReferences: ui.showDemandReferences,
        visibleReferenceTrackIds: ui.visibleReferenceTrackIds,
        referenceTrackKeys: ui.referenceTrackKeys,
        externalReferenceTracks: ui.externalReferenceTracks,
        sourceTrack: ui.sourceTrack,
        timetableTab: ui.timetableTab,
        patternType: ui.patternType,
        analysisResult: effectiveAnalysisResult,
        pendingChangeSet: localEditState.pendingChangeSet,
        airportConstraintsMap: airportConstraintsQuery.constraintsMap,
        aircraftAvailabilityWindowsByRegistration: availabilityWindowsQuery.windowsByRegistration,
        isAircraftAvailabilityValidationPending,
        currentEditingTrackId,
        timelineFocusWindow: ui.timelineFocusWindow,
        localValidationIssues,
        canDeleteTrack,
        isAgentWorking,
        enableTimelineProjection: shouldBuildTimelineProjection,
        enablePatternProjection: shouldBuildTimelineProjection,
    });

    useScheduleDraftEditorSideEffects({ ui, data, draftId });

    const recalculateCommand = useCallback(
        (command) => buildChangeSet(command, localEditState.localFlightsMapRef.current, command.meta?.referenceData || {}, airportConstraintsQuery.constraintsMap),
        [airportConstraintsQuery.constraintsMap, localEditState.localFlightsMapRef],
    );

    const validateCommand = useCallback(
        (command) => validateFlightConstraints(command, localEditState.localFlightsMapRef.current, airportConstraintsQuery.constraintsMap, availabilityWindowsQuery.windowsByRegistration),
        [airportConstraintsQuery.constraintsMap, availabilityWindowsQuery.windowsByRegistration, localEditState.localFlightsMapRef],
    );

    const editPipeline = useScheduleDraftEditPipeline({
        validateCommand,
        recalculateCommand,
        applyChangeSet: localEditState.applyChangeSet,
        refetchAnalysis: debouncedRefreshAnalysis,
    });

    const wiringCallbacks = buildWiringCallbacks(ui);
    const mutations = useScheduleDraftEditorMutations({
        draftId,
        defaultTrackId,
        currentEditingTrackId,
        editPipeline,
        hasLocalWorkingChanges,
        onDraftFlightsChanged: debouncedRefreshAnalysis,
        ...wiringCallbacks,
        onDeleteTrackSuccess: (track, meta) => {
            wiringCallbacks.onDeleteTrackSuccess?.(track, meta);
            const deletedTrackId = track?.id || meta?.trackId;
            wiringCallbacks.onTrackDeleted?.(deletedTrackId);
            localCompareMatchState.discardTrack?.(deletedTrackId);
        },
    });

    const value = useMemo(
        () => ({
            draftId,
            permissions: {
                canUpdateFlight,
                canDeleteFlight,
                canManageTrack: canUpdateFlight,
                canDeleteTrack,
                canTriggerAgent: rawCanUpdateFlight && !isAgentWorking && !hasLocalWorkingChanges,
                hasUpdatePermission: rawCanUpdateFlight,
            },
            data: {
                draft,
                serverDraft,
                currentEditingTrack,
                localEditState,
                localCompareMatchState,
                agentSessionStatus,
                isAgentWorking,
                agentSessions: agentSessionsQuery.data?.sessions || [],
                agentSessionsStatus: agentSessionsQuery.data?.agent_session_status || agentSessionStatus,
                agentSessionsError: agentSessionsQuery.error,
                isFetchingAgentSessions: agentSessionsQuery.isFetching,
                refetchAgentSessions: agentSessionsQuery.refetch,
                error: detailQuery.error,
                isLoading: detailQuery.isLoading,
                isFetching: detailQuery.isFetching,
                isAnalyzing: analysisQuery.isFetching,
                analysisResult: effectiveAnalysisResult,
                analysisError: analysisQuery.error,
                airportConstraintsMap: airportConstraintsQuery.constraintsMap,
                aircraftAvailabilityWindowsByRegistration: availabilityWindowsQuery.windowsByRegistration,
                aircraftAvailabilityWindowsError: availabilityWindowsQuery.error,
                isFetchingAircraftAvailabilityWindows: availabilityWindowsQuery.isFetching,
                refetchAircraftAvailabilityWindows: availabilityWindowsQuery.refetch,
                validationIssues: localValidationIssues,
                localValidationIssues,
                refetchAnalysis: refetchAnalysisWithCurrentWorkingFlights,
                refetchDraft: detailQuery.refetch,
                ...data,
            },
            ui,
            mutations,
            editPipeline,
        }),
        [agentSessionStatus, agentSessionsQuery.data?.agent_session_status, agentSessionsQuery.data?.sessions, agentSessionsQuery.error, agentSessionsQuery.isFetching, agentSessionsQuery.refetch, analysisQuery.error, analysisQuery.isFetching, airportConstraintsQuery.constraintsMap, availabilityWindowsQuery.error, availabilityWindowsQuery.isFetching, availabilityWindowsQuery.refetch, availabilityWindowsQuery.windowsByRegistration, canDeleteFlight, canDeleteTrack, canUpdateFlight, currentEditingTrack, currentEditingTrackId, data, defaultTrackId, detailQuery.error, detailQuery.isFetching, detailQuery.isLoading, detailQuery.refetch, draft, draftId, editPipeline, effectiveAnalysisResult, hasLocalWorkingChanges, isAircraftAvailabilityValidationPending, isAgentWorking, localCompareMatchState, localEditState, localValidationIssues, mutations, rawCanUpdateFlight, refetchAnalysisWithCurrentWorkingFlights, serverDraft, ui],
    );

    return <ScheduleDraftEditorWorkspaceContext.Provider value={value}>{children}</ScheduleDraftEditorWorkspaceContext.Provider>;
}

export default ScheduleDraftEditorWorkspaceProvider;
